import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { MarketCredit } from '@sorare/core/src/atoms/icons/MarketCredit';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { CornerBadge } from '@sorare/core/src/components/CornerBadge';
import { filters } from '@sorare/core/src/lib/glossary';

import { AmountSaved } from '#src/components/marketCredit/AmountSaved/index.tsx';
import { useGetAuctionDetails } from '#src/hooks/offers/useGetAuctionDetails.ts';
import { useIsPrimaryBuyLive } from '#src/hooks/primaryBuy/useIsPrimaryBuyLive.ts';

import type { MarketCreditCornerBadge_anyCard } from './__generated__/index.graphql.ts';

const Label = styled(LabelS)`
  white-space: nowrap;
`;

const GreenCornerBadge = styled(CornerBadge).attrs({
  color: 'var(--c-gradient-discount)',
})``;

type Props = {
  canUseConversionCredit: boolean;
  anyCard: MarketCreditCornerBadge_anyCard;
};

export const MarketCreditCornerBadge = ({
  anyCard,
  canUseConversionCredit,
}: Props) => {
  const auction = useGetAuctionDetails(anyCard.latestEnglishAuction);
  const primaryBuy = useIsPrimaryBuyLive(anyCard);

  if (primaryBuy && canUseConversionCredit)
    return (
      <AmountSaved
        Wrapper={GreenCornerBadge}
        price={primaryBuy.price}
        sport={anyCard.sport}
      />
    );
  if (auction && canUseConversionCredit)
    return (
      <GreenCornerBadge color="var(--c-gradient-discount)">
        <MarketCredit width={16} height={16} />
        <Label bold color="var(--c-black)">
          <FormattedMessage {...filters.creditEligibleFilter} />
        </Label>
      </GreenCornerBadge>
    );
  return null;
};

MarketCreditCornerBadge.fragments = {
  anyCard: gql`
    fragment MarketCreditCornerBadge_anyCard on AnyCardInterface {
      slug
      sport
      latestEnglishAuction {
        id
        ...useGetAuctionDetails_auction
      }
      ...useIsPrimaryBuyLive_anyCard
    }
    ${useGetAuctionDetails.fragments.auction}
    ${useIsPrimaryBuyLive.fragments.anyCard}
  ` as TypedDocumentNode<MarketCreditCornerBadge_anyCard>,
};
